import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiOrganizationChart } from 'react-icons/ri';
import { apiService } from '../../components/Config/api.service';

/**
 * Ce qui manque à l'organigramme (Yros/Kavero#469, T5, contrat C30 en
 * commentaire du ticket). Chaque nombre mène à l'action qui le corrige :
 * les salariés sans responsable et les écarts avec le valideur des congés
 * filtrent la liste, les départements sans responsable ouvrent leur panneau.
 *
 * C'est une aide : un nombre nul est masqué, la barre disparaît quand rien ne
 * manque, et une panne la masque sans bruit.
 */
function IndicateurOrganigramme({ onFiltrer, onVoirDepartements }) {
  const { t, i18n } = useTranslation();
  const [etat, setEtat] = useState(null);
  const nombre = (n) => new Intl.NumberFormat(i18n?.language || 'fr-FR').format(n);

  useEffect(() => {
    let actif = true;
    // `Promise.resolve().then(...)` : une erreur levée avant toute promesse
    // est rattrapée comme un refus, au lieu de faire planter la page.
    Promise.resolve()
      .then(() => apiService.lireIndicateurOrganigramme())
      .then((reponse) => { if (actif) setEtat(reponse?.ok ? reponse : null); })
      .catch(() => { if (actif) setEtat(null); });
    return () => { actif = false; };
  }, []);

  if (!etat) return null;
  const elements = [];
  if (etat.salaries_sans_responsable > 0) {
    elements.push(
      <button key="salaries" type="button" className="font-medium text-[--color-primary] hover:underline"
        onClick={() => onFiltrer({
          field: 'parent_id', operator: '=', value: false,
          label: t('organigrammeIndicateur.filtreSansResponsable') })}>
        {t('organigrammeIndicateur.sansResponsable', { n: nombre(etat.salaries_sans_responsable) })}
      </button>);
  }
  if (etat.departements_sans_responsable > 0) {
    elements.push(
      <button key="departements" type="button" className="font-medium text-[--color-primary] hover:underline"
        onClick={onVoirDepartements}>
        {t('organigrammeIndicateur.departements', { n: nombre(etat.departements_sans_responsable) })}
      </button>);
  }
  if (etat.ecarts_conges.length > 0) {
    elements.push(
      <button key="ecarts" type="button" className="font-medium text-[--color-primary] hover:underline"
        onClick={() => onFiltrer({
          field: 'id', operator: 'in', value: etat.ecarts_conges,
          label: t('organigrammeIndicateur.filtreEcarts') })}>
        {t('organigrammeIndicateur.ecarts', { n: nombre(etat.ecarts_conges.length) })}
      </button>);
  }
  if (elements.length === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600">
      <RiOrganizationChart className="h-4 w-4 shrink-0 text-[--color-primary]" aria-hidden="true" />
      <span>{t('organigrammeIndicateur.titre')}</span>
      {elements.map((element, index) => (
        <React.Fragment key={element.key}>
          {index > 0 && <span aria-hidden="true" className="text-gray-300">·</span>}
          {element}
        </React.Fragment>
      ))}
    </div>
  );
}

export default IndicateurOrganigramme;
